import React, { useState } from 'react';
import { Typography, Space, Row, notification, Modal, Input, Dropdown } from 'antd';
import { useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import {DeleteForeverRounded, EditRounded, AddRounded, GroupAddRounded} from '@mui/icons-material';
import useCompany from '../../../Company/useCompany';
import {addPortfoliosToCompany} from "../../../../api/companyApi";
import useCopyToClipboard from "../../../../hooks/useClipboard";
import {usePortfolioGroups} from "../../usePortfolioGroups";
import { createOrUpdatePortfolioGroup } from '../../../../api/userApi';

const SelectedPortfoliosController = ({ selectedKeys, onDelete }) => {
    const dispatch = useDispatch();
    const { allowAdminActions, companyDetails } = useCompany();
    const userId = localStorage.getItem('uid');
    const copyToClipboard = useCopyToClipboard();
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const { portfolioGroups } = usePortfolioGroups();

    const handleCreateGroup = () => {
        dispatch(createOrUpdatePortfolioGroup({
            name: newGroupName,
            portfolio_ids: selectedKeys
        }))
        .unwrap()
        .then(() => {
            setCreateModalVisible(false);
            setNewGroupName('');
            notification.success({
                message: 'Success',
                description: 'Portfolio group created successfully'
            });
        })
        .catch((error) => {
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to create group'
            });
        });
    };

    const handleAddToExistingGroup = (group) => {
        const updatedPortfolioIds = [...new Set([...group.portfolio_ids, ...selectedKeys])];

        dispatch(createOrUpdatePortfolioGroup({
            group_id: group.group_id,
            name: group.name,
            portfolio_ids: updatedPortfolioIds
        }))
        .unwrap()
        .then(() => {
            notification.success({
                message: 'Success',
                description: 'Portfolios added to group successfully'
            });
        })
        .catch((error) => {
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to update group'
            });
        });
    };

    const handleAddToCompany = () => {
        dispatch(addPortfoliosToCompany({userId, portfolioIds: selectedKeys}))
            .then(() => {
                notification.success({
                    message: 'Portfolios added to company',
                    description: 'Selected portfolios have been added to the company',
                });
            })
            .catch((error) => {
                notification.error({
                    message: 'Error adding portfolios to company',
                    description: error.message,
                });
            });
    }

    const handleCopyIds = () => {
        copyToClipboard(JSON.stringify(selectedKeys));
    }

    // Ant Design dropdown menu items
    const getMenuItems = (portfolioGroups = []) => ({
        items: [
            {
                key: 'create',
                label: 'Create New Group',
                icon: <GroupAddRounded />,
                onClick: () => setCreateModalVisible(true)
            },
            ...(portfolioGroups.length ? [
                { type: 'divider' },
                {
                    key: 'existing',
                    label: 'Existing Groups',
                    type: 'group',
                    children: portfolioGroups.map(group => ({
                        key: group.group_id,
                        label: group.name,
                        onClick: () => handleAddToExistingGroup(group)
                    }))
                }
            ] : [])
        ]
    });

    if (selectedKeys.length === 0) return null;

    return (
        <Row justify={'space-between'} align={'middle'} className={"sticky top-0 z-50 shadow-lg py-3 pl-8 pr-12 round-2 my-3"}>
            <Typography.Text style={{ marginRight: 'auto', fontWeight: '300' }}>
                Selected Portfolios: {selectedKeys.length}
            </Typography.Text>
            <Space>
                <Button onClick={handleCopyIds}>
                    Copy IDs
                </Button>

                <Dropdown
                    menu={getMenuItems(portfolioGroups)}
                    placement="bottomRight"
                >
                    <Button startIcon={<GroupAddRounded />}>
                        Portfolio Groups
                    </Button>
                </Dropdown>

                {allowAdminActions === true && (
                    <Button startIcon={<AddRounded/>} onClick={handleAddToCompany}>
                        Add to Company
                    </Button>
                )}
                <Button
                    danger
                    className={'square'}
                    startIcon={<DeleteForeverRounded />}
                    onClick={onDelete}
                    variant={'contained'}
                    color={'error'}
                >
                    Delete Portfolios
                </Button>
            </Space>

            <Modal
                title="Create New Portfolio Group"
                open={createModalVisible}
                onOk={handleCreateGroup}
                onCancel={() => {
                    setCreateModalVisible(false);
                    setNewGroupName('');
                }}
                okButtonProps={{ disabled: !newGroupName.trim() }}
            >
                <Input
                    placeholder="Enter group name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    style={{ marginTop: 16 }}
                />
            </Modal>
        </Row>
    );
};

export default SelectedPortfoliosController;